import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ProfileMenu } from "@/components/ProfileMenu";
import { useAuth } from "@/lib/auth";
import { Plus, Star, Settings, Trash2, Clock, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { getUserMealPlans, removeMealPlan } from "@/lib/mealPlan";
import { Badge } from "@/components/ui/badge";
import { translations, cuisineTypes } from "@/lib/translations";

export default function MealPlansPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mealPlans, setMealPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMealPlans();
  }, []);

  const loadMealPlans = async () => {
    try {
      const plans = await getUserMealPlans();
      setMealPlans(plans || []);
    } catch (error) {
      console.error("Error loading meal plans:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePlan = async (planId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await removeMealPlan(planId);
      setMealPlans(mealPlans.filter((plan) => plan.id !== planId));
    } catch (error) {
      console.error("Error removing meal plan:", error);
    }
  };

  if (loading) {
    return (
      <div className="w-full min-h-screen bg-gray-50 p-4 md:p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-50">
      {/* Header */}
      <div className="w-full bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-2 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img
              src="/PlateMate_logo.svg"
              alt="PlateMate"
              className="h-10 w-auto"
            />
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/favorites")}
            >
              <Star className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/settings")}
            >
              <Settings className="h-5 w-5" />
            </Button>
            {user && <ProfileMenu user={user} />}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <Button
            onClick={() => navigate("/new-plan")}
            className="rtl-button-icon"
            size="lg"
          >
            <Plus className="h-5 w-5 ml-2" />
            צור תוכנית חדשה
          </Button>
          <h1 className="text-2xl font-semibold">תוכניות הארוחות שלי</h1>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {mealPlans.map((plan) => (
            <Card
              key={`${plan.id}-${plan.created_at}`}
              className="p-6 hover:shadow-md transition-shadow cursor-pointer relative group"
              onClick={() => navigate(`/meal-plan/${plan.id}`)}
            >
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={(e) => handleRemovePlan(plan.id, e)}
              >
                <Trash2 className="h-5 w-5" />
              </Button>

              <div className="space-y-4">
                <div className="flex flex-row-reverse justify-between items-start">
                  <h3 className="text-xl font-semibold text-right">
                    תוכנית ל-{plan.plan_data.days.length} ימים
                  </h3>
                  <span className="text-sm text-muted-foreground text-right">
                    {new Date(plan.created_at).toLocaleDateString("he-IL")}
                  </span>
                </div>

                <div className="space-y-4 text-right">
                  {/* Dietary Preferences */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-right">
                      {translations.mealPlanner.dietary.title}:
                    </p>
                    <div className="flex flex-wrap gap-2 justify-end">
                      {Object.entries(plan.preferences.dietary)
                        .filter(([_, value]) => value)
                        .map(([key]) => (
                          <Badge key={key} variant="secondary" className="text-right">
                            {
                              translations.mealPlanner.dietary[
                                key as keyof typeof translations.mealPlanner.dietary
                              ]
                            }
                          </Badge>
                        ))}
                    </div>
                  </div>

                  {/* Cooking Time */}
                  <div className="flex flex-row-reverse items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>
                      {plan.preferences.cookingTime}{" "}
                      {translations.common.minutes}
                    </span>
                  </div>

                  {/* Servings */}
                  <div className="flex flex-row-reverse items-center gap-2 text-sm text-muted-foreground">
                    <Users className="h-4 w-4" />
                    <span>
                      {plan.preferences.servings}{" "}
                      {translations.mealPlanner.planSettings.servingsCount}
                    </span>
                  </div>

                  {/* Cuisines */}
                  {plan.preferences.cuisines?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-right">
                        {translations.mealPlanner.preview.preferredCuisines}:
                      </p>
                      <div className="flex flex-wrap gap-2 justify-end">
                        {plan.preferences.cuisines.map((cuisine: string) => (
                          <Badge key={cuisine} variant="outline" className="text-right">
                            {cuisineTypes[
                              cuisine as keyof typeof cuisineTypes
                            ] || cuisine}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
